// بخش «پرونده بالینی» صفحه مراجعه‌کننده (AP-IAM-04؛ US-IAM-033، 040، 041 — C-AGENT-400؛ محتوا از AP-CLN-01: SCR-STAFF-501). باز کردن آن
// GET patients/{id}/clinical-access است (سیاست دید بالینی؛ مشاهده Audit می‌شود). خارج از دامنه (404) ← فقط هویت (AC-IAM-033.2) و پیشنهاد
// دسترسی اضطراری برای دارنده iam.break_glass.use (COMP-BREAK-GLASS)؛ در بازه دسترسی اضطراری نوار قرمز با زمان پایان (AC-IAM-041.1).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type AccessOverrideView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState, toJalali, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ClinicalRecord } from '../clinical/ClinicalRecord';
import { BreakGlassDialog } from './BreakGlassDialog';
import { iamKeys } from './common';

export interface ClinicalRecordPanelProps {
  patientId: string;
}

export function ClinicalRecordPanel({ patientId }: ClinicalRecordPanelProps) {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const canBreakGlass = access.can('iam.break_glass.use');

  const record = useQuery({
    queryKey: iamKeys.clinicalAccess(session?.tenantId, patientId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/identity/patients/{patientId}/clinical-access', { params: { path: { patientId } } })),
    retry: false,
    staleTime: 60_000,
  });

  const close = useMutation({
    mutationFn: (overrideId: string) =>
      unwrap(tenantApi.POST('/api/v1/identity/access-overrides/{id}:close', { params: { path: { id: overrideId } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: iamKeys.clinicalAccess(session?.tenantId, patientId) }),
  });

  const time = (value: string | null | undefined) => toJalali(value, { timeZone, pattern: 'HH:mm' });
  const onActivated = (view: AccessOverrideView) => {
    setDialog(false);
    setNotice(t('iam:break_glass.activated', { time: time(view.expiresAt) }));
    void queryClient.invalidateQueries({ queryKey: iamKeys.clinicalAccess(session?.tenantId, patientId) });
  };

  if (record.isPending) {
    return <LoadingState rows={2} />;
  }

  const denied = isApiError(record.error) && record.error.status === 404;
  if (record.error && !denied) {
    return <ErrorState error={record.error} onRetry={() => void record.refetch()} />;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="clinical-record" data-state={denied ? 'denied' : record.data?.decision}>
      <Typography variant="h2" sx={{ mb: 1.5 }}>
        {t('iam:clinical.title')}
      </Typography>
      {denied ? (
        <Stack spacing={1.5} alignItems="flex-start">
          <Alert severity="warning" data-testid="clinical-restricted" sx={{ alignSelf: 'stretch' }}>
            <strong>{t('iam:clinical.restricted_title')}</strong>
            <div>{t('iam:clinical.restricted_body')}</div>
          </Alert>
          {canBreakGlass && (
            <Button variant="outlined" color="error" onClick={() => setDialog(true)} data-testid="break-glass-open">
              {t('iam:clinical.break_glass_offer')}
            </Button>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {record.data?.decision === 'AllowViaOverride' && (
            <Alert
              severity="error"
              variant="filled"
              data-testid="break-glass-banner"
              action={
                record.data.overrideId ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => close.mutate(record.data?.overrideId ?? '')}
                    disabled={close.isPending}
                    data-testid="break-glass-end"
                  >
                    {t('iam:clinical.end_override')}
                  </Button>
                ) : undefined
              }
            >
              {t('iam:clinical.break_glass_banner', { time: time(record.data.overrideExpiresAt) })}
            </Alert>
          )}
          <Alert severity="info" data-testid="clinical-allowed">
            {t('iam:clinical.allowed')}
          </Alert>
          {!record.data?.canViewSensitive && (
            <Typography variant="body2" color="text.secondary">
              {t('iam:clinical.sensitive_hidden')}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" data-testid="clinical-policy">
            {t('iam:clinical.policy', {
              policy: t(`iam:policies.clinical_options.${record.data?.policy ?? 'BranchScoped'}.title`),
            })}
          </Typography>
          {/* AP-CLN-01: محتوای پرونده بالینی (SCR-STAFF-501) جایگزین پوسته IAM-04. */}
          <ClinicalRecord patientId={patientId} />
        </Stack>
      )}
      {dialog && (
        <BreakGlassDialog open={dialog} patientId={patientId} onClose={() => setDialog(false)} onActivated={onActivated} />
      )}
      <Snackbar open={notice !== null} autoHideDuration={4000} onClose={() => setNotice(null)} message={notice} />
    </Paper>
  );
}
